export type FieldId = string;
export type UnitFamily =
	| 'LengthLong'
	| 'Length'
	| 'Pressure'
	| 'Flow'
	| 'Area'
	| 'Volume'
	| 'ElectricCurrent'
	| 'Power'
	| 'Rotation'
	| 'Velocity';

/**
 * Comprehensive mapping of units to their corresponding UnitFamily categories
 */
export const UNIT_FAMILY_MAP: Record<string, UnitFamily> = {
	// Length measurements (small dimensions)
	mm: 'Length', // Millimeters - for pipes, components, dimensions

	// Length measurements (long distances/heights/elevations)
	m: 'LengthLong', // Meters - for distances, depths, heights
	mAD: 'LengthLong', // Meters above datum - for elevations, levels

	// Pressure measurements
	mH2O: 'Pressure', // Meters head of water

	// Flow measurements
	'm3/s': 'Flow', // Cubic meters per second

	// Area measurements
	m2: 'Area', // Square meters

	// Volume measurements
	m3: 'Volume', // Cubic meters

	// Electric current measurements
	A: 'ElectricCurrent', // Amperes

	// Power measurements
	kW: 'Power', // Kilowatts

	// Rotational speed measurements
	rpm: 'Rotation', // Revolutions per minute

	// Velocity measurements
	'm/s': 'Velocity', // Meters per second
};

/**
 * Units that don't map to any UnitFamily (dimensionless, currency, time, etc.)
 */
export const UNMAPPED_UNITS = new Set([
	'$', // Currency
	's', // Time (seconds)
	'm/m', // Gradient/slope (dimensionless ratio)
]);

/**
 * Gets the UnitFamily for a given unit string, throwing on unknown units
 * @param unit - The unit string to map
 * @returns The corresponding UnitFamily, or undefined for explicitly unmappable units
 * @throws Error if the unit is not explicitly handled (either mappable or unmappable)
 */
export function getUnitFamilyOrFail(unit: string): UnitFamily | undefined {
	// Check if unit is mappable
	if (unit in UNIT_FAMILY_MAP) {
		return UNIT_FAMILY_MAP[unit];
	}

	// Check if unit is explicitly unmappable
	if (UNMAPPED_UNITS.has(unit)) {
		return undefined;
	}

	// Unit is not handled - throw an exception
	throw new Error(
		`Unknown unit '${unit}'. All units must be explicitly defined as either mappable or unmappable. ` +
			`Mappable units: [${Object.keys(UNIT_FAMILY_MAP).join(', ')}]. ` +
			`Unmappable units: [${Array.from(UNMAPPED_UNITS).join(', ')}].`,
	);
}
